<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import AlternativesViewDependenciesList from '@Alternatives/AlternativesViews/Components/AlternativesViewDependenciesList.vue'
    import type { AlternativesViewDeleterDialogViewModel } from '@Alternatives/AlternativesViews/Components/AlternativesViewDeleterDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: AlternativesViewDeleterDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'delete-list', id: string | null): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const initialized = computed(() => vm.value?.initialized ?? false)

    const canDelete = computed(() => vm.value?.hasDependants === false)

    const name = computed(() => vm.value.name)
    const dependenciesCount = computed(() => vm.value.dependenciesCount)

    const reports = computed(() => vm.value.reports)
    const lists = computed(() => vm.value.lists)
    const estimationsSurveys = computed(() => vm.value.estimationsSurveys)

    // ---------------- methods ----------------

    function deleteList()
    {
        emit('delete-list', vm.value.id)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header" :class="{'bg-danger': canDelete}">
                        <h5 class="modal-title">Delete attribute</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h4 v-if="!initialized">
                            <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                        </h4>
                        <div v-if="canDelete">
                            <h4>Are you sure you want to delete this list?</h4>
                            <p>You are about to delete '<strong>{{name}}</strong>'. This can't be undone.</p>
                        </div>
                        <div v-if="!canDelete">
                            <div class="alert alert-danger text-dark" role="alert">
                                <h5 class="alert-heading">Cannot delete this list</h5>
                                <p>
                                    The list '<strong>{{name}}</strong>' is used in <strong>{{dependenciesCount}}</strong> places.
                                    To delete this list, first remove it from where it is used.
                                </p>
                            </div>
                            <AlternativesViewDependenciesList :initialized="initialized"
                                                              :reports="reports"
                                                              :lists="lists"
                                                              :estimationsSurveys="estimationsSurveys" />
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button v-if="canDelete" type="button" class="btn btn-danger" @click="deleteList">Delete</button>
                        <button type="button" class="btn btn-outline-secondary" @click="close">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .modal-header.bg-danger .modal-title,
    .modal-header.bg-danger .close {
        color: white;
    }
</style>